import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Typography, Box, Button, Alert, CircularProgress, Paper, TextField,
  IconButton, Divider, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PushPinIcon from '@mui/icons-material/PushPin';
import ReplyIcon from '@mui/icons-material/Reply';
import Layout from '../components/Layout';
import forumService from '../services/forumService';
import { useUnion } from '../contexts/UnionContext';

const MAX_DEPTH = 4;

function ReplyNode({ reply, depth, topicId, isUnionAdmin, canEdit, canDelete, onReload, formatDate, isEdited }) {
  const { t } = useTranslation();
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyBody, setReplyBody] = useState('');
  const [replying, setReplying] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editBody, setEditBody] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);

  const handleReply = async () => {
    if (!replyBody.trim()) return;
    setReplying(true);
    try {
      await forumService.createReply({ topic: topicId, parent: reply.id, body: replyBody.trim() });
      setReplyBody('');
      setReplyOpen(false);
      await onReload();
    } catch { /* handled by parent */ }
    finally { setReplying(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await forumService.updateReply(reply.id, { body: editBody });
      setEditing(false);
      await onReload();
    } catch { /* handled by parent */ }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await forumService.deleteReply(reply.id);
      await onReload();
    } catch { /* handled by parent */ }
    finally { setDeleteDialog(false); }
  };

  return (
    <Box sx={{ ml: depth > 0 ? 3 : 0 }}>
      <Paper sx={{ p: 2, mb: 1.5, borderLeft: depth > 0 ? '3px solid' : 'none', borderLeftColor: 'divider' }} variant="outlined">
        {editing ? (
          <Box>
            <TextField
              fullWidth value={editBody}
              onChange={(e) => setEditBody(e.target.value)}
              multiline rows={3} sx={{ mb: 1 }}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" variant="contained" onClick={handleSave} disabled={saving}>
                {saving ? <CircularProgress size={16} /> : t('common.save')}
              </Button>
              <Button size="small" onClick={() => setEditing(false)}>{t('common.cancel')}</Button>
            </Box>
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>{reply.author_name || reply.author_username}</strong> &middot; {formatDate(reply.created_at)}
                {isEdited(reply.created_at, reply.updated_at) && (
                  <Chip label={t('forum.edited')} size="small" variant="outlined" sx={{ ml: 1 }} />
                )}
              </Typography>
              <Box>
                {depth < MAX_DEPTH && (
                  <IconButton size="small" onClick={() => setReplyOpen(!replyOpen)} title={t('forum.reply')}>
                    <ReplyIcon fontSize="small" />
                  </IconButton>
                )}
                {(reply.is_own || isUnionAdmin) && canEdit && (
                  <IconButton size="small" onClick={() => { setEditBody(reply.body); setEditing(true); }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}
                {(reply.is_own || isUnionAdmin) && canDelete && (
                  <IconButton size="small" onClick={() => setDeleteDialog(true)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Box>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{reply.body}</Typography>
          </>
        )}

        {replyOpen && (
          <Box sx={{ mt: 1.5, pl: 1, borderLeft: '2px solid', borderColor: 'primary.light' }}>
            <TextField
              fullWidth size="small" placeholder={t('forum.replyPlaceholder')}
              value={replyBody} onChange={(e) => setReplyBody(e.target.value)}
              multiline rows={2} sx={{ mb: 1 }}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" variant="contained" onClick={handleReply} disabled={replying || !replyBody.trim()}>
                {replying ? <CircularProgress size={16} /> : t('forum.reply')}
              </Button>
              <Button size="small" onClick={() => { setReplyOpen(false); setReplyBody(''); }}>{t('common.cancel')}</Button>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Nested children */}
      {reply.children?.map((child) => (
        <ReplyNode
          key={child.id} reply={child} depth={depth + 1}
          topicId={topicId} isUnionAdmin={isUnionAdmin}
          canEdit={canEdit} canDelete={canDelete}
          onReload={onReload} formatDate={formatDate} isEdited={isEdited}
        />
      ))}

      {/* Delete confirmation */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>{t('forum.deleteReply')}</DialogTitle>
        <DialogContent>
          <Typography>{t('forum.deleteReplyMsg')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>{t('common.cancel')}</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>{t('common.delete')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function ForumTopic() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { isUnionAdmin, forumMode } = useUnion();
  const canEdit = isUnionAdmin || forumMode !== 'WRITABLE';
  const canDelete = isUnionAdmin || forumMode === 'DELETABLE';

  const [topic, setTopic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Top-level reply
  const [replyBody, setReplyBody] = useState('');
  const [replying, setReplying] = useState(false);

  // Edit topic
  const [editingTopic, setEditingTopic] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editBody, setEditBody] = useState('');
  const [savingTopic, setSavingTopic] = useState(false);

  // Delete topic
  const [deleteTopicDialog, setDeleteTopicDialog] = useState(false);

  const loadTopic = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await forumService.getTopic(id);
      setTopic(data);
    } catch {
      setError(t('forum.failedLoadTopic'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => { loadTopic(); }, [loadTopic]);

  const handleReply = async () => {
    if (!replyBody.trim()) return;
    setReplying(true);
    try {
      await forumService.createReply({ topic: parseInt(id), body: replyBody.trim() });
      setReplyBody('');
      await loadTopic();
    } catch {
      setError(t('forum.failedPostReply'));
    } finally {
      setReplying(false);
    }
  };

  const handleSaveTopic = async () => {
    setSavingTopic(true);
    try {
      await forumService.updateTopic(id, { title: editTitle, body: editBody });
      setEditingTopic(false);
      await loadTopic();
    } catch {
      setError(t('forum.failedUpdateTopic'));
    } finally {
      setSavingTopic(false);
    }
  };

  const handleDeleteTopic = async () => {
    try {
      await forumService.deleteTopic(id);
      navigate('/forum');
    } catch {
      setError(t('forum.failedDeleteTopic'));
    } finally {
      setDeleteTopicDialog(false);
    }
  };

  const handleTogglePin = async () => {
    try {
      await forumService.updateTopic(id, { is_pinned: !topic.is_pinned });
      await loadTopic();
    } catch {
      setError(t('forum.failedTogglePin'));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('et-EE', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const isEdited = (created, updated) => {
    if (!created || !updated) return false;
    return Math.abs(new Date(updated) - new Date(created)) > 2000;
  };

  const countAllReplies = (replies) => {
    if (!replies) return 0;
    return replies.reduce((sum, r) => sum + 1 + countAllReplies(r.children), 0);
  };

  if (loading) {
    return (
      <Layout>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  if (!topic) {
    return (
      <Layout>
        <Alert severity="error">{t('forum.topicNotFound')}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/forum')} sx={{ mt: 2 }}>
          {t('forum.backToForum')}
        </Button>
      </Layout>
    );
  }

  const totalReplies = countAllReplies(topic.replies);

  return (
    <Layout>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/forum')} sx={{ mb: 2 }}>
        {t('forum.backToForum')}
      </Button>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Topic */}
      <Paper sx={{ p: 3, mb: 3 }}>
        {editingTopic ? (
          <Box>
            <TextField
              fullWidth label={t('forum.titleLabel')} value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth label={t('forum.bodyLabel')} value={editBody}
              onChange={(e) => setEditBody(e.target.value)}
              multiline rows={4} sx={{ mb: 2 }}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="contained" onClick={handleSaveTopic} disabled={savingTopic}>
                {savingTopic ? <CircularProgress size={20} /> : t('common.save')}
              </Button>
              <Button onClick={() => setEditingTopic(false)}>{t('common.cancel')}</Button>
            </Box>
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  {topic.is_pinned && <PushPinIcon fontSize="small" color="primary" />}
                  <Typography variant="h5">{topic.title}</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {topic.author_name || topic.author_username} &middot; {formatDate(topic.created_at)}
                  {isEdited(topic.created_at, topic.updated_at) && (
                    <Chip label={t('forum.edited')} size="small" variant="outlined" sx={{ ml: 1 }} />
                  )}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {isUnionAdmin && (
                  <IconButton size="small" onClick={handleTogglePin} title={topic.is_pinned ? t('forum.unpin') : t('forum.pin')}>
                    <PushPinIcon fontSize="small" color={topic.is_pinned ? 'primary' : 'disabled'} />
                  </IconButton>
                )}
                {(topic.is_own || isUnionAdmin) && canEdit && (
                  <IconButton size="small" onClick={() => {
                    setEditTitle(topic.title);
                    setEditBody(topic.body);
                    setEditingTopic(true);
                  }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}
                {(topic.is_own || isUnionAdmin) && canDelete && (
                  <IconButton size="small" onClick={() => setDeleteTopicDialog(true)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{topic.body}</Typography>
          </>
        )}
      </Paper>

      {/* Replies */}
      <Typography variant="h6" sx={{ mb: 2 }}>
        {t('forum.replyCount', { count: totalReplies })}
      </Typography>

      {topic.replies?.map((r) => (
        <ReplyNode
          key={r.id} reply={r} depth={0}
          topicId={parseInt(id)} isUnionAdmin={isUnionAdmin}
          canEdit={canEdit} canDelete={canDelete}
          onReload={loadTopic} formatDate={formatDate} isEdited={isEdited}
        />
      ))}

      {/* Top-level reply form */}
      <Paper sx={{ p: 2, mt: 2 }} variant="outlined">
        <TextField
          fullWidth placeholder={t('forum.replyPlaceholder')} value={replyBody}
          onChange={(e) => setReplyBody(e.target.value)}
          multiline rows={3} sx={{ mb: 1 }}
        />
        <Button
          variant="contained" onClick={handleReply}
          disabled={replying || !replyBody.trim()}
        >
          {replying ? <CircularProgress size={20} /> : t('forum.reply')}
        </Button>
      </Paper>

      {/* Delete topic confirmation */}
      <Dialog open={deleteTopicDialog} onClose={() => setDeleteTopicDialog(false)}>
        <DialogTitle>{t('forum.deleteTopic')}</DialogTitle>
        <DialogContent>
          <Typography>{t('forum.deleteTopicMsg')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTopicDialog(false)}>{t('common.cancel')}</Button>
          <Button color="error" variant="contained" onClick={handleDeleteTopic}>{t('common.delete')}</Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}

export default ForumTopic;
